import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FireIcon, TrophyIcon } from '@heroicons/react/24/outline';
import type { CurationAgreementStat, CurationLeaderboardRow } from '@avisoma/shared';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { Button } from '../../catalyst/button';
import { SortableTable, type SortableTableColumn } from '../../components/SortableTable';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatDateOnly } from '../../lib/dateFormat';
import backend from '../../services/backend';
import { PeriodToggle, type Period } from './PeriodToggle';
import { ActivitySparkline } from './ActivitySparkline';

type LeaderboardDisplayRow = {
  reviewerId: string;
  reviewerName: string | null;
  total: number;
  corrected: number;
  confirmed: number;
  currentStreak: number;
  bestStreak: number;
  activity: number[];
  activitySum: number;
  lastReviewAt: string | null;
  secondOpinionsGiven: number;
  agreementRate: number | null;
  rank: number;
};

const MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

// Whole-percent, or an em-dash when there's nothing to rate (null) — never "0%",
// which would read as total disagreement rather than "no data yet".
const formatRate = (rate: number | null): string => (rate === null ? '—' : `${Math.round(rate * 100)}%`);

// Rank reflects the selected period's total (computed before handing to
// SortableTable), so medals stay put regardless of which column the user sorts by.
function toDisplayRows(rows: CurationLeaderboardRow[], period: Period): LeaderboardDisplayRow[] {
  const mapped = rows.map((row) => {
    const bucket = period === 'all' ? row : row.windows[period];
    return {
      reviewerId: row.reviewerId,
      reviewerName: row.reviewerName,
      total: bucket.total,
      corrected: bucket.corrected,
      confirmed: bucket.confirmed,
      currentStreak: row.currentStreak,
      bestStreak: row.bestStreak,
      activity: row.activity,
      activitySum: row.activity.reduce((sum, n) => sum + n, 0),
      lastReviewAt: row.lastReviewAt,
      secondOpinionsGiven: row.secondOpinionsGiven,
      agreementRate: row.agreementRate,
    };
  });

  const rankById = new Map(
    [...mapped]
      .sort((a, b) => b.total - a.total)
      .map((row, index) => [row.reviewerId, index + 1] as const),
  );

  return mapped.map((row) => ({ ...row, rank: rankById.get(row.reviewerId)! }));
}

const columns: SortableTableColumn<LeaderboardDisplayRow>[] = [
  {
    label: 'Rank',
    accessor: 'rank',
    sortable: true,
    render: (row) =>
      row.total > 0 && MEDALS[row.rank] ? (
        <span className="text-lg">{MEDALS[row.rank]}</span>
      ) : (
        <span className="text-gray-400 dark:text-zinc-500">{row.rank}</span>
      ),
  },
  {
    label: 'Curator',
    accessor: 'reviewerName',
    sortable: true,
    render: (row) => (
      <span className="font-medium text-gray-900 dark:text-zinc-100">
        {row.reviewerName ?? row.reviewerId}
      </span>
    ),
  },
  { label: 'Total', accessor: 'total', sortable: true },
  { label: 'Corrected', accessor: 'corrected', sortable: true },
  { label: 'Confirmed', accessor: 'confirmed', sortable: true },
  {
    // All-time second opinions (reviews on pages someone else reviewed first) with the
    // share that agreed — em-dash when the curator hasn't given any. Not period-scoped.
    label: '2nd opinions',
    accessor: 'secondOpinionsGiven',
    sortable: true,
    render: (row) =>
      row.secondOpinionsGiven > 0 ? (
        <span className="text-gray-900 dark:text-zinc-100">
          {row.secondOpinionsGiven}
          <span className="text-gray-400 dark:text-zinc-500"> · {formatRate(row.agreementRate)} agree</span>
        </span>
      ) : (
        <span className="text-gray-400 dark:text-zinc-500">—</span>
      ),
  },
  {
    label: 'Streak',
    accessor: 'currentStreak',
    sortable: true,
    render: (row) =>
      row.currentStreak > 0 ? (
        <span className="inline-flex items-center gap-1 text-gray-900 dark:text-zinc-100">
          <FireIcon className="size-4 text-orange-500 dark:text-orange-400" />
          {row.currentStreak}
          {row.bestStreak > row.currentStreak && (
            <span className="text-gray-400 dark:text-zinc-500">· best {row.bestStreak}</span>
          )}
        </span>
      ) : (
        <span className="text-gray-400 dark:text-zinc-500">—</span>
      ),
  },
  {
    label: '14-day activity',
    accessor: 'activitySum',
    sortable: true,
    render: (row) => <ActivitySparkline activity={row.activity} />,
  },
  {
    label: 'Last review',
    accessor: 'lastReviewAt',
    sortable: true,
    render: (row) => (
      <span className="text-gray-500 dark:text-zinc-400">
        {row.lastReviewAt ? formatDateOnly(row.lastReviewAt) : '—'}
      </span>
    ),
  },
];

// Global inter-reviewer agreement — the share of double-reviewed pages the second
// curator confirmed. Em-dash until any page has ≥2 distinct reviewers (DR-1736).
function AgreementCard({ agreement }: { agreement: CurationAgreementStat }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Inter-reviewer agreement
        </p>
        <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-zinc-100">
          {formatRate(agreement.agreementRate)}
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
          across {agreement.doubleReviewedPages} double-reviewed{' '}
          {agreement.doubleReviewedPages === 1 ? 'page' : 'pages'}
        </p>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  useDocumentTitle('Curation leaderboard');
  const { user } = useAuth();
  const [period, setPeriod] = useState<Period>('all');

  // Key by the viewer's id + effective role: the endpoint returns role-scoped
  // data (admin/supervisor → all curators, otherwise → own), and admins can
  // emulate roles — so the cache must not be shared across identities/roles.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['curation-leaderboard', user?.id, user?.role],
    queryFn: async () => (await backend.getCurationLeaderboard()).data,
  });

  const displayRows = useMemo(() => toDisplayRows(data?.rows ?? [], period), [data, period]);

  const scopeNote =
    data?.scope === 'all' ? 'Review stats across all curators' : 'Your review stats';

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Curation leaderboard"
        description={scopeNote}
        icon={<TrophyIcon className="size-6" />}
      />

      {isLoading && (
        <div className="space-y-4">
          <Skeleton variant="rect" className="h-64 w-full" />
        </div>
      )}

      {!isLoading && error && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 p-8 text-center space-y-4">
          <p className="text-red-600 dark:text-red-400 text-sm">Failed to load the leaderboard.</p>
          <Button outline onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {data?.agreement && <AgreementCard agreement={data.agreement} />}

          <PeriodToggle value={period} onChange={setPeriod} />

          <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 overflow-hidden">
            {displayRows.length > 0 ? (
              <SortableTable
                columns={columns}
                data={displayRows}
                rowKey={(row) => row.reviewerId}
                storageId="curation-leaderboard"
                defaultSortBy="total"
                defaultSortDir="desc"
              />
            ) : (
              <p className="p-8 text-center text-sm text-gray-500 dark:text-zinc-400">
                No reviews recorded yet.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
